'use client';

import { matchesPublicCategoryProductsSeed, type PublicCategoryProductsSeed } from '@eliadmin/core/src/components/categories/public-category-products-seed';
import { buildProductsRequest } from '@eliadmin/core/src/api/products/product-list-request';
import type { Category } from '@eliadmin/core/src/api/slugs/types/slug-detail-types';

import { CacheKey } from '@eliadmin/core/src/api/constants';
import { fetchProducts } from '@eliadmin/core/src/api/products/product-api';
import { useCategorySort } from '@eliadmin/core/src/hooks/category/use-category-sort';
import { useTranslations } from '@eliadmin/core/src/hooks/use-translations';
import { useCategoryStore } from '@eliadmin/core/src/stores/category-store';
import { useCustomerStore } from '@eliadmin/core/src/stores/customer-store';
import { useGlobalStore } from '@eliadmin/core/src/stores/global-store';
import { formatPrice } from '@eliadmin/core/src/utils/price-utils';
import { buildProductsQuery } from '@eliadmin/core/src/utils/product-utils';
import { cn } from '@eliadmin/core/src/utils/style-utils';
import { useSuspenseInfiniteQuery } from '@tanstack/react-query';
import { useSearchParams } from 'next/navigation';
import { memo, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Paragraph } from '../ui/paragraph';
import CategorySort from '@eliadmin/core/src/components/categories/category-sort';
import { CategoryFilterSheet } from '@eliadmin/core/src/components/categories/category-filter-sheet';
import { AttributeType } from '@eliadmin/core/src/stores/category-store';
import { ProductCard, ProductCardSkeleton } from '../product/product-card';
import { Pagination } from '@eliadmin/core/src/components/ui/pagination';
import { Button } from '../ui/button';
import { CategoryFilterSelect } from './category-filter-select';
import { IoMdCloseCircle } from 'react-icons/io';

type ActiveFilterChip = {
  key: string;
  label: string;
  value: string;
  onRemove: () => void;
};

const FilteredValueChip = memo(function FilteredValueChip({
  label,
  value,
  onRemove,
}: Omit<ActiveFilterChip, 'key'>) {
  return (
    <button
      type="button"
      onClick={onRemove}
      className={cn(
        'flex cursor-pointer items-center justify-start gap-2 transition-all hover:opacity-80',
        'ea-category-content__filtered-value',
      )}
    >
      <Paragraph size="base" color="neutral">
        {label}:&nbsp;
        <strong className="font-semibold text-foreground">{value}</strong>
      </Paragraph>
      <IoMdCloseCircle size={20} className="max-h-5 min-h-5 min-w-5 max-w-5 text-[#6E6D7B]" />
    </button>
  );
});

export type CategoryProps = {
  initialProducts?: PublicCategoryProductsSeed;
  className?: {
    grid_wrapper?: string;
    product?: string;
  };
  category: Category;
  onProductTotalChange?: (total: number) => void;
  paginationProps?: { scrollToElementAfterPageChange?: string };
  locale: string;
};

export function CategoryContent(props: CategoryProps) {
  const isInitialized = useCustomerStore(state => state.isInitialized);

  if (!isInitialized && !props.initialProducts) return <CategoryContentSkeleton />;

  return <CategoryContentQuery {...props} />;
}

function CategoryContentQuery(props: CategoryProps) {
  const authToken = useCustomerStore(s => s.me?.authToken);
  const filters = useCategoryStore(s => s.filter);
  const category = useCategoryStore(s => s.category);
  const setFilter = useCategoryStore(s => s.setFilter);
  const currency = useGlobalStore(s => s.generalLayout.currency);
  const currencySettings = useGlobalStore(s => s.generalLayout.currency_settings);
  const { VALUES: sortValues } = useCategorySort();
  const { t } = useTranslations();

  const manufacturerNames = useMemo(
    () => new Map((category.manufacturers ?? []).map(item => [item.id, item.name])),
    [category.manufacturers],
  );
  const statusNames = useMemo(
    () => new Map((category.statuses ?? []).map(item => [item.id, item.name])),
    [category.statuses],
  );
  const availabilityNames = useMemo(
    () => new Map((category.availabilities ?? []).map(item => [item.id, item.name])),
    [category.availabilities],
  );
  const supplierNames = useMemo(() => {
    const map = new Map<number, string>();
    (category.suppliers ?? []).forEach(item => map.set(item.id, item.name));
    filters.suppliers_pins.forEach(pin => map.set(pin.id, pin.name));
    return map;
  }, [category.suppliers, filters.suppliers_pins]);
  const attributeDefinitions = useMemo(() => {
    const map = new Map<
      number,
      { attribute: NonNullable<Category['attributes']>[number]; values: Map<number, string> }
    >();
    (category.attributes ?? []).forEach(attribute => {
      if (attribute.id == null) return;
      map.set(attribute.id, {
        attribute,
        values: new Map((attribute.values ?? []).map(value => [value.id, value.name])),
      });
    });
    return map;
  }, [category.attributes]);

  const categoryMinPrice = category.price?.min ?? 0;
  const categoryMaxPrice = category.price?.max ?? 0;

  const activeFilters = useMemo<ActiveFilterChip[]>(() => {
    const chips: ActiveFilterChip[] = [];

    if (filters.sort.order && filters.sort.orderBy) {
      const sortLabel = sortValues.find(
        item => item.value === `${filters.sort.orderBy}:${filters.sort.order}`,
      )?.label;
      if (sortLabel) {
        chips.push({
          key: 'sort',
          label: t('text341'),
          value: t(sortLabel),
          onRemove: () => setFilter({ sort: { order: null, orderBy: null } }),
        });
      }
    }

    filters.manufacturers.forEach(id => {
      chips.push({
        key: `manufacturer-${id}`,
        label: t('text195'),
        value: manufacturerNames.get(id) ?? String(id),
        onRemove: () => setFilter({ manufacturer: id }),
      });
    });

    filters.statuses.forEach(id => {
      chips.push({
        key: `status-${id}`,
        label: t('text518'),
        value: statusNames.get(id) ?? String(id),
        onRemove: () => setFilter({ status: id }),
      });
    });

    filters.availabilities.forEach(id => {
      chips.push({
        key: `availability-${id}`,
        label: t('text339'),
        value: availabilityNames.get(id) ?? String(id),
        onRemove: () => setFilter({ availability: id }),
      });
    });

    filters.suppliers.forEach(id => {
      chips.push({
        key: `supplier-${id}`,
        label: t('text718'),
        value: supplierNames.get(id) ?? String(id),
        onRemove: () => setFilter({ supplier: id }),
      });
    });

    filters.attributes.forEach(({ id: attributeId, values }) => {
      const definition = attributeDefinitions.get(attributeId);
      if (!definition) return;

      values.forEach(valueId => {
        const valueName = definition.values.get(valueId);
        if (valueName === undefined) return;

        chips.push({
          key: `attribute-${attributeId}-${valueId}`,
          label: definition.attribute.name,
          value: valueName,
          onRemove: () =>
            setFilter({
              selectedValue: {
                attribute: definition.attribute,
                value: { id: valueId, name: valueName },
              },
            }),
        });
      });
    });

    const isPriceFiltered =
      (filters.price_min > 0 && filters.price_min > categoryMinPrice) ||
      (filters.price_max > 0 && filters.price_max < categoryMaxPrice);

    if (isPriceFiltered) {
      chips.push({
        key: 'price',
        label: t('text340'),
        value: `${formatPrice(filters.price_min || categoryMinPrice, currency, currencySettings)} - ${formatPrice(filters.price_max || categoryMaxPrice, currency, currencySettings)}`,
        onRemove: () => setFilter({ price_min: categoryMinPrice, price_max: categoryMaxPrice }),
      });
    }

    return chips;
  }, [
    filters.sort.order,
    filters.sort.orderBy,
    filters.manufacturers,
    filters.statuses,
    filters.availabilities,
    filters.suppliers,
    filters.attributes,
    filters.attributes,
    filters.price_min,
    filters.price_max,
    sortValues,
    manufacturerNames,
    statusNames,
    availabilityNames,
    supplierNames,
    attributeDefinitions,
    categoryMinPrice,
    categoryMaxPrice,
    currency,
    currencySettings,
    setFilter,
    t,
  ]);

  const generalLayout = useGlobalStore(state => state.generalLayout);

  const selectAttributes = useMemo(
    () =>
      (category.attributes ?? [])
        .filter(a => a.type === AttributeType.SELECT && a.name && a.values?.length)
        .slice(0, 3),
    [category.attributes],
  );
  const searchParams = useSearchParams();

  const currentPage = searchParams.get('page');

  const attributesKey = JSON.stringify(filters.attributes ?? []);


  const queryWithoutPage = useMemo(
    () =>
      buildProductsQuery({
        attributes: filters.attributes,
        categories: [props.category?.id],
        manufacturers: filters.manufacturers ?? [],
        results: generalLayout?.pagination?.paginationProducts,
        order: filters.sort.order,
        orderBy: filters.sort.orderBy,
        // values: filters.attributes.flatMap(attr => attr.values),
        price_from: filters.price_min,
        price_to: filters.price_max,
        availabilities: filters.availabilities,
        suppliers: filters.suppliers,
      }),
    [
      props.category?.id,
      filters.manufacturers,
      generalLayout?.pagination?.paginationProducts,
      filters.sort.order,
      filters.sort.orderBy,
      filters.price_min,
      filters.price_max,
      filters.availabilities,
      filters.suppliers,
      filters.attributes,
    ],
  );
  const queryWithoutPageKey = JSON.stringify(queryWithoutPage);

  const productRequestUri = buildProductsRequest({ ...queryWithoutPage, page: Number(currentPage ?? 1) }).uri;

  const productsQuery = useSuspenseInfiniteQuery({
    initialData: matchesPublicCategoryProductsSeed(props.initialProducts, {
      locale: props.locale, requestUri: productRequestUri, authToken, page: Number(currentPage ?? 1),
    }) ? { pages: [props.initialProducts!.data], pageParams: [Number(currentPage ?? 1)] } : undefined,
    initialDataUpdatedAt: props.initialProducts?.updatedAt,
    queryKey: [
      CacheKey.PRODUCTS,
      'infinite',
      currentPage,
      queryWithoutPageKey,
      authToken,
      props.locale,
      attributesKey,
    ],
    queryFn: ({ pageParam }) => {
      return fetchProducts({
        query: {
          ...queryWithoutPage,
          page: pageParam,
        },
        locale: props.locale,
        authToken,
      });
    },
    getNextPageParam: lastPage => {
      if (lastPage?.meta?.current_page < lastPage?.meta?.last_page)
        return lastPage?.meta?.current_page + 1;
      return undefined;
    },
    initialPageParam: currentPage ? parseInt(currentPage) : 1,
    refetchOnMount: false,
    refetchOnWindowFocus: false,
    staleTime: 1000 * 60 * 5,
  });

  const lastPage = productsQuery.data?.pages[productsQuery.data?.pages.length - 1];
  const flatList = productsQuery.data?.pages.flatMap(page => page.data) ?? [];
  const [loadMode, setLoadMode] = useState<'manual' | 'auto'>('manual');
  const [fetchTrigger, setFetchTrigger] = useState<'load-more' | 'show-all' | 'auto' | null>(null);
  const autoLoadRef = useRef<HTMLDivElement | null>(null);

  const fetchNextPageSafely = useCallback(
    async (trigger: 'load-more' | 'show-all' | 'auto') => {
      if (!productsQuery.hasNextPage) return false;
      if (productsQuery.isFetchingNextPage) return false;
      setFetchTrigger(trigger);
      try {
        await productsQuery.fetchNextPage();
        return true;
      } finally {
        setFetchTrigger(null);
      }
    },
    [productsQuery],
  );

  useEffect(() => {
    if (loadMode !== 'auto') return;
    if (!productsQuery.hasNextPage) return;

    const target = autoLoadRef.current;
    if (!target) return;

    const observer = new IntersectionObserver(
      entries => {
        const entry = entries[0];
        if (!entry?.isIntersecting) return;
        void fetchNextPageSafely('auto');
      },
      { root: null, rootMargin: '250px 0px', threshold: 0 },
    );

    observer.observe(target);

    return () => {
      observer.disconnect();
    };
  }, [fetchNextPageSafely, loadMode, productsQuery.hasNextPage]);

  useEffect(() => {
    setLoadMode('manual');
    setFetchTrigger(null);
  }, [currentPage, props.locale, authToken, queryWithoutPageKey, attributesKey]);

  useEffect(() => {
    props.onProductTotalChange?.(lastPage?.meta?.total);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lastPage?.meta?.total]);

  const hasProducts = flatList.length > 0;

  return (
    <>
      <section
        id="category-content"
        className={cn(
          'custom-section',
          'z-[35] flex flex-col items-center justify-center gap-6 bg-background py-3',
          'ea-category-content__actions-bar',
        )}
      >
        <div
          className={cn(
            'custom-container',
            'flex flex-col items-center justify-center gap-4 lg:flex-row lg:items-start lg:justify-between',
          )}
        >
          <div className="flex w-full flex-col items-start justify-start gap-6">
            <div className="grid w-full grid-cols-2 items-center justify-start gap-3 md:flex md:w-fit">
              <CategorySort />
              <CategoryFilterSheet />
            </div>
            {activeFilters.length > 0 && (
              <div className="flex w-full flex-wrap items-start justify-start gap-2 sm:gap-4 xl:gap-6">
                {activeFilters.map(chip => (
                  <FilteredValueChip
                    key={chip.key}
                    label={chip.label}
                    value={chip.value}
                    onRemove={chip.onRemove}
                  />
                ))}
              </div>
            )}
          </div>
          {!!lastPage?.meta?.total && (
            <Paragraph
              className="w-full whitespace-nowrap text-center lg:w-fit lg:pt-[14px] lg:text-right"
              size="base"
              color="neutral"
            >
              {t('text2053')} {lastPage.meta.from} - {lastPage.meta.to} z {lastPage.meta.total}{' '}
              výsledkov
            </Paragraph>
          )}
        </div>
      </section>
      <section
        className={cn(
          'custom-section',
          'gap-6 pb-8 pt-8 sm:gap-8 sm:pb-10 sm:pt-10 md:gap-10 lg:gap-12 lg:pb-12 lg:pt-12',
        )}
      >
        <div className={cn('custom-container', 'flex items-start justify-between gap-4 lg:gap-6')}>
          <div className="grid w-full grid-cols-2 gap-x-2 gap-y-6 sm:gap-x-4 md:grid-cols-3 lg:grid-cols-4 xl:gap-x-6">
            {hasProducts ? (
              flatList.map(product => <ProductCard product={product} key={product.slug} />)
            ) : (
              <Paragraph
                size="lg"
                color="muted-foreground"
                className="col-span-full mb-4 mt-12 text-center"
              >
                {t('text3687')}
              </Paragraph>
            )}
          </div>
        </div>
        {loadMode === 'manual' && !!productsQuery.hasNextPage && (
          <div
            className={cn(
              'flex w-full flex-col justify-center gap-2 md:flex-row',
              'ea-category-products__load-more',
            )}
          >
            <Button
              variant="primary-foreground"
              isLoading={productsQuery.isFetchingNextPage && fetchTrigger === 'load-more'}
              className="w-full sm:w-fit"
              onClick={() => void fetchNextPageSafely('load-more')}
            >
              {t('text338')}
            </Button>
          </div>
        )}

        {loadMode === 'auto' && !!productsQuery.hasNextPage && (
          <div
            className={cn(
              'flex flex-col items-center justify-center gap-2',
              'ea-category-products__auto-load',
            )}
          >
            <div ref={autoLoadRef} className="h-1 w-full" />
            {!!productsQuery.isFetchingNextPage && (
              <div className="h-6 w-6 animate-spin rounded-full border-2 border-muted border-t-foreground" />
            )}
          </div>
        )}
        <div className={cn('flex flex-row justify-center', 'ea-category-products__pagination')}>
          <Pagination
            links={lastPage?.meta.links}
            scrollToElementAfterPageChange={
              props.paginationProps?.scrollToElementAfterPageChange || undefined
            }
          />
        </div>
      </section>
    </>
  );
}

export function CategoryContentSkeleton() {
  return (
    <section className={cn('custom-section', 'animate-pulse', 'gap-12')}>
      <div className={cn('custom-container', 'hidden items-center justify-between sm:flex')}>
        <div className="flex items-center justify-start gap-3">
          <div className="h-12 w-[160px] rounded-[20px] bg-neutral-100" />
        </div>
        <div className="flex items-center justify-end gap-12">
          <div className="h-[18px] w-[105px] rounded-[20px] bg-neutral-100" />
          <div className="h-12 w-[175px] rounded-[20px] bg-neutral-100" />
        </div>
      </div>
      <div className="flex w-full flex-col items-center justify-center gap-3 sm:hidden">
        <div className="h-12 w-full bg-neutral-100" />
        <div className="h-12 w-full bg-neutral-100" />
        <div className="mt-3 h-4 w-[40%] bg-neutral-100" />
      </div>
      <div
        className={cn(
          'custom-container',
          'grid w-full grid-cols-2 gap-x-2 gap-y-4 sm:gap-x-3 sm:gap-y-6 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5',
        )}
      >
        <ProductCardSkeleton amount={10} variant="vertical" />
      </div>
    </section>
  );
}
